import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Button,
} from '@mui/material';
import {
  Download as DownloadIcon,
} from '@mui/icons-material';
import { PageHeader, Loading, EmptyState } from '../../components/Layout';
import { toast } from 'react-toastify';
import api from '../../services/api';

const TenantPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/payments/me/payments');
      setPayments(response.data || []);
    } catch (error) {
      console.log('No payments found or error loading payments:', error);
      setPayments([]);
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        console.error('Error loading payments:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
      case 'mpesa':
        return 'success';
      case 'bank':
      case 'bank_transfer':
        return 'primary';
      case 'cash':
        return 'default';
      case 'credit':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentMethodLabel = (method, isCreditPayment) => {
    if (isCreditPayment || method === 'credit') {
      return 'CREDIT';
    }
    return method?.toUpperCase() || 'CASH';
  };

  const handleDownloadReceipt = (payment) => {
    const isCreditPayment = payment.is_credit_payment || payment.payment_method === 'credit';
    const creditGenerated = payment.credit_generated || 0;
    const paymentMethodDisplay = isCreditPayment ? 'CREDIT BALANCE' : payment.payment_method?.toUpperCase();
    const transactionRef = isCreditPayment ? 'Auto-applied from credit balance' : (payment.transaction_ref || 'N/A');
    
    // Create receipt HTML
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Payment Receipt - ${payment.invoice_number}</title>
        <style>
          body {
            font-family: 'Arial', sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px;
            color: #333;
          }
          .receipt-header {
            text-align: center;
            border-bottom: 3px solid #2196F3;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .receipt-header h1 {
            color: #2196F3;
            margin: 0;
            font-size: 32px;
          }
          .receipt-header p {
            margin: 5px 0;
            color: #666;
          }
          .receipt-info {
            background: #f5f5f5;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px solid #ddd;
          }
          .info-row:last-child {
            border-bottom: none;
          }
          .info-label {
            font-weight: bold;
            color: #666;
          }
          .info-value {
            color: #333;
          }
          .amount-section {
            background: #e3f2fd;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            margin: 30px 0;
          }
          .amount-label {
            font-size: 16px;
            color: #666;
            margin-bottom: 10px;
          }
          .amount-value {
            font-size: 36px;
            font-weight: bold;
            color: #2196F3;
          }
          .credit-notice {
            background: #e8f5e9;
            padding: 15px;
            border-radius: 8px;
            border-left: 4px solid #4CAF50;
            margin: 20px 0;
          }
          .credit-notice-title {
            font-weight: bold;
            color: #2e7d32;
            margin-bottom: 5px;
          }
          .credit-notice-text {
            color: #1b5e20;
            font-size: 14px;
          }
          .receipt-footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #ddd;
            color: #999;
            font-size: 12px;
          }
          .status-badge {
            display: inline-block;
            padding: 8px 16px;
            background: #4CAF50;
            color: white;
            border-radius: 20px;
            font-weight: bold;
            margin-top: 10px;
          }
          .credit-badge {
            display: inline-block;
            padding: 8px 16px;
            background: #2196F3;
            color: white;
            border-radius: 20px;
            font-weight: bold;
            margin-top: 10px;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="receipt-header">
          <h1>PAYMENT RECEIPT</h1>
          <p>Property Management System</p>
          <div class="${isCreditPayment ? 'credit-badge' : 'status-badge'}">${isCreditPayment ? 'CREDIT PAYMENT' : 'PAID'}</div>
        </div>
        
        <div class="receipt-info">
          <div class="info-row">
            <span class="info-label">Receipt Date:</span>
            <span class="info-value">${new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Date:</span>
            <span class="info-value">${new Date(payment.payment_date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Invoice Number:</span>
            <span class="info-value">#${payment.invoice_number || payment.invoice_id}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Method:</span>
            <span class="info-value">${paymentMethodDisplay}</span>
          </div>
          <div class="info-row">
            <span class="info-label">${isCreditPayment ? 'Payment Source:' : 'Transaction Reference:'}</span>
            <span class="info-value">${transactionRef}</span>
          </div>
        </div>
        
        ${isCreditPayment ? `
        <div class="credit-notice">
          <div class="credit-notice-title">✓ Credit Payment Applied</div>
          <div class="credit-notice-text">This payment was automatically applied from your available credit balance.</div>
        </div>
        ` : ''}
        
        <div class="amount-section">
          <div class="amount-label">Amount Paid</div>
          <div class="amount-value">KES ${payment.amount?.toLocaleString()}</div>
        </div>
        
        ${creditGenerated > 0 ? `
        <div class="credit-notice">
          <div class="credit-notice-title">✓ Credit Generated from Overpayment</div>
          <div class="credit-notice-text">
            An overpayment of <strong>KES ${creditGenerated.toLocaleString()}</strong> has been credited to your account for future use.
          </div>
        </div>
        ` : ''}
        
        <div class="receipt-footer">
          <p>This is a computer-generated receipt and is valid without signature.</p>
          <p>Thank you for your payment!</p>
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    // Create a blob and download
    const blob = new Blob([receiptHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${payment.invoice_number || payment.id}-${new Date().getTime()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Receipt downloaded successfully!');
  };

  if (loading) {
    return <Loading message="Loading payments..." />;
  }

  return (
    <Box>
      <PageHeader
        title="My Payments"
        subtitle="View your payment history"
        breadcrumbs={[{ label: 'Payments' }]}
      />

      {payments.length === 0 ? (
        <EmptyState
          title="No Payment History"
          description="You haven't made any payments yet. Your payment history will appear here once you start making payments."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Invoice</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Method</strong></TableCell>
                <TableCell><strong>Reference</strong></TableCell>
                <TableCell><strong>Notes</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => {
                const isCreditPayment = payment.is_credit_payment || payment.payment_method === 'credit';
                const creditGenerated = payment.credit_generated || 0;
                
                return (
                  <TableRow key={payment.id}>
                    <TableCell>
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{payment.invoice_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {payment.amount?.toLocaleString()}
                      </Typography>
                      {creditGenerated > 0 && (
                        <Typography variant="caption" color="success.main" display="block">
                          +{creditGenerated.toLocaleString()} credit generated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPaymentMethodLabel(payment.payment_method, isCreditPayment)}
                        color={getPaymentMethodColor(payment.payment_method)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {isCreditPayment ? 'Auto-applied from credit' : (payment.transaction_ref || 'N/A')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isCreditPayment ? (
                        <Chip
                          label="Credit Auto-Applied"
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      ) : creditGenerated > 0 ? (
                        <Chip
                          label="Overpayment"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadReceipt(payment)}
                      >
                        Receipt
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TenantPayments;
